import { Badge, Button, Flex, IconButton, Text } from '@radix-ui/themes';
import { ClipboardCopy, Minus, Plus, Square, X } from 'lucide-react';
import type { Session } from '@mac-remote/protocol';

/**
 * Switch projects without going back to the list, and end the one you are in.
 *
 * The tab chips still appear only from two sessions up — a tab bar with a single tab
 * is just a thinner header. "Завершить" is different: it shows whenever a session is
 * open, at any count.
 *
 * That distinction is the fix for a real bug. Closing used to live solely on the `X`
 * inside the active chip, and the whole bar returned null below two sessions — so in
 * the ordinary case of exactly one open session there was no way to close it from the
 * app at all, and sessions accumulated on the Mac until someone reached for tmux by
 * hand.
 */
export function SessionTabs({
  sessions,
  active,
  busy,
  label,
  onSelect,
  onClose,
  onAdd,
  onCopyText,
  fontSize,
  onFontSize,
}: {
  sessions: Session[];
  active: string | null;
  busy: Set<string>;
  /** Session ids are wire identifiers; the root's is a sentinel. Print through this. */
  label: (sid: string) => string;
  onSelect: (sid: string) => void;
  onClose: (sid: string) => void;
  onAdd: () => void;
  /** Open the session's output as selectable text. A long press on the terminal does
   *  the same thing, but a gesture nobody can see is not an answer to "how do I copy
   *  this?" — the button is here to be found. */
  onCopyText: () => void;
  /** Terminal type size. It lives here rather than in the app header because it is a
   *  property of the session you are looking at, and because the header had run out
   *  of room — two icon buttons there were enough to start clipping the status text
   *  beside them. This row scrolls sideways and has space to spare. */
  fontSize: number;
  onFontSize: (next: number) => void;
}) {
  if (!active) return null;
  const multiple = sessions.length > 1;

  return (
    <div className="keybar" style={{ borderBottom: '1px solid var(--gray-a4)', alignItems: 'center' }}>
      {multiple &&
        sessions.map((s) => {
          const on = s.id === active;
          return (
            <Button
              key={s.id}
              size="1"
              variant={on ? 'solid' : 'soft'}
              color={busy.has(s.id) ? 'jade' : 'gray'}
              onClick={() => onSelect(s.id)}
            >
              {busy.has(s.id) && !on && (
                <Badge radius="full" color="jade" variant="solid" style={{ width: 6, height: 6, padding: 0 }} />
              )}
              <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label(s.id)}
              </span>
              {on && (
                <X
                  size={13}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(s.id);
                  }}
                />
              )}
            </Button>
          );
        })}

      <Button size="1" variant="ghost" color="gray" onClick={onAdd} title="Открыть ещё проект">
        <Plus size={14} />
      </Button>

      {/* Pinned right, so it never lands under a thumb reaching for a tab. */}
      <Flex align="center" gap="1" style={{ marginLeft: 'auto', flex: '0 0 auto' }}>
        <Button size="1" variant="soft" color="gray" title="Текст сессии — выделить и скопировать" onClick={onCopyText}>
          <ClipboardCopy size={13} />
          Текст
        </Button>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          title="Мельче шрифт"
          onClick={() => onFontSize(Math.max(7, fontSize - 1))}
        >
          <Minus size={14} />
        </IconButton>
        <Text size="1" style={{ color: 'var(--app-hint)', minWidth: 16, textAlign: 'center' }}>
          {fontSize}
        </Text>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          title="Крупнее шрифт"
          onClick={() => onFontSize(Math.min(20, fontSize + 1))}
        >
          <Plus size={14} />
        </IconButton>
      </Flex>

      <Button size="1" variant="soft" color="red" style={{ flex: '0 0 auto' }} onClick={() => onClose(active)}>
        <Square size={11} />
        Завершить
      </Button>
    </div>
  );
}
